import produce from "immer";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchProjects,
  fetchProject,
  updateProject,
  createProject,
  deleteProject,
} from "../api/project";
import { normalizeError, hasMeaningfulErrors } from "../operationFeedback";

const initialState = {
  isLoading: false,
  error: false,
  errors: null,
  data: [],
  totalCount: 0,
  currentCount: 0,
  newCount: 0,
};

export const initialController = {
  filters: [],
  page: 0,
  query: "",
  sort: "desc",
  sortBy: "created_at",
  view: "all",
};

function rejectNormalized(rejectWithValue, err, operationKey) {
  return rejectWithValue(normalizeError(err, { operationKey }));
}

function isHttpFailureStatus(status) {
  const n = Number(status);
  return Number.isFinite(n) && n >= 400;
}

const GENERIC_SAFE =
  /^(En uventet feil oppstod\.|Handlingen kunne ikke fullføres\.)$/;

function rejectedErrors(action, fallbackNb) {
  const msg = action.payload?.safeMessage;
  if (typeof msg === "string" && msg.trim() && !GENERIC_SAFE.test(msg)) {
    return [msg];
  }
  return [fallbackNb];
}

export const deleteProjectAsync = createAsyncThunk(
  "projects/deleteProjectAsync",
  async (payload, { rejectWithValue }) => {
    try {
      return await deleteProject(payload);
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "delete_project");
    }
  }
);

export const createProjectAsync = createAsyncThunk(
  "projects/createProjectAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createProject(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "create_project");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "create_project");
    }
  }
);

export const updateProjectAsync = createAsyncThunk(
  "projects/updateProjectAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateProject(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "update_project");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "update_project");
    }
  }
);

export const getProjectsAsync = createAsyncThunk(
  "projects/getProjectsAsync",
  async (controller = initialController, { rejectWithValue }) => {
    try {
      const response = await fetchProjects(controller);
      const status = response?.response?.status ?? response?.status;
      if (
        isHttpFailureStatus(status) ||
        hasMeaningfulErrors(response?.errors)
      ) {
        return rejectNormalized(rejectWithValue, response, "list_projects");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "list_projects");
    }
  }
);

export const getProjectAsync = createAsyncThunk(
  "projects/getProjectAsync",
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetchProject(id);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "get_project");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "get_project");
    }
  }
);

const projectSlice = createSlice({
  name: "projects",
  initialState,
  reducers: {
    patchProjectInList(state, action) {
      const updated = action.payload;
      if (!updated?.id || !Array.isArray(state.data)) return;
      const index = state.data.findIndex((project) => project.id === updated.id);
      if (index >= 0) {
        state.data[index] = { ...state.data[index], ...updated };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProjectsAsync.pending, (state, action) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
        if (!action.meta?.arg?.append) {
          state.data = [];
          state.totalCount = 0;
        }
      })
      .addCase(getProjectsAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.meta?.arg?.append) {
          state.data = [...(state.data || []), ...(action.payload?.data || [])];
        } else {
          state.data = action.payload.data;
        }
        state.totalCount = action.payload.totalCount;
        state.currentCount = state.data.length;
        state.newestProjectsCount = action.payload.newCount;
      })
      .addCase(getProjectsAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente prosjektene.");
      })
      .addCase(updateProjectAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(updateProjectAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          const index = draftState.data.findIndex(
            (type) => type.id === action.payload?.id
          );
          if (index >= 0) {
            draftState.data[index] = action.payload;
          }
          draftState.isLoading = false;
        });
      })
      .addCase(updateProjectAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke oppdatere prosjektet.");
      })
      .addCase(createProjectAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(createProjectAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          if (action.payload?.id) {
            draftState.data.push(action.payload);
          }
          draftState.isLoading = false;
        });
      })
      .addCase(createProjectAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke opprette prosjektet.");
      })
      .addCase(deleteProjectAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(deleteProjectAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          const id =
            action.meta?.arg?.project?.project_id ??
            action.meta?.arg?.project?.id ??
            action.payload?.id;
          if (id != null && Array.isArray(draftState.data)) {
            draftState.data = draftState.data.filter((row) => row.id !== id);
          } else if (Array.isArray(action.payload)) {
            draftState.data = action.payload;
          }
          draftState.isLoading = false;
        });
      })
      .addCase(deleteProjectAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke slette prosjektet.");
      })
      .addCase(getProjectAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getProjectAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.project = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(getProjectAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente prosjektet.");
      });
  },
});

export const { patchProjectInList } = projectSlice.actions;

export const { reducer } = projectSlice;

export default projectSlice;
